import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { SolanaProvider } from './contexts/SolanaProvider';
import LandingPage from './pages/LandingPage';
import Dashboard from './pages/Dashboard';
import UserSettings from './pages/UserSettings';
import LiveSupport from './pages/LiveSupport';
import OrderSuccess from './pages/OrderSuccess';
import AdminLogin from './pages/AdminLogin';
import AdminPanel from './pages/AdminPanel';
import NotFound from './pages/NotFound';
import ApiDeveloper from './pages/ApiDeveloper';
import TermsAndConditions from './pages/TermsAndConditions';
import PrivacyPolicy from './pages/PrivacyPolicy';
import License from './pages/License';
import BuyDexscreenerReactions from './pages/BuyDexscreenerReactions';
import BuyDexmoji from './pages/BuyDexmoji';
import DexscreenerReaction from './pages/DexscreenerReaction';
import BuyRocketDexscreener from './pages/BuyRocketDexscreener';
import BoostDexscreener from './pages/BoostDexscreener';
import BuyDexscreenerRockets from './pages/BuyDexscreenerRockets';
import BuyDexscreenerFire from './pages/BuyDexscreenerFire';
import BuyDexscreenerPoop from './pages/BuyDexscreenerPoop';
import Dexreactions from './pages/Dexreactions';
import CheapDexscreenerReactions from './pages/CheapDexscreenerReactions';
import BuyDexRockets from './pages/BuyDexRockets';
import OrderStatus from './pages/OrderStatus';
import Pricing from './pages/Pricing';
import GtagInjector from './components/GtagInjector';
import { authAPI } from './services/api';
import '@/App.css';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        // Try to load user from localStorage first for faster initial render
        const cachedUser = localStorage.getItem('user');
        if (cachedUser) {
          try {
            setUser(JSON.parse(cachedUser));
          } catch (e) {
            console.error('Error parsing cached user:', e);
          }
        }
        
        // Then fetch fresh data from backend
        const response = await authAPI.getMe();
        setUser(response.data);
        localStorage.setItem('user', JSON.stringify(response.data));
      } catch (error) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  };

  const handleLogin = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <SolanaProvider>
      <GtagInjector />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage user={user} onLogin={handleLogin} onLogout={handleLogout} />} />
          <Route 
            path="/dashboard" 
            element={user ? <Dashboard user={user} onLogout={handleLogout} /> : <Navigate to="/" />} 
          />
          <Route 
            path="/settings" 
            element={user ? <UserSettings user={user} onLogout={handleLogout} /> : <Navigate to="/" />} 
          />
          <Route 
            path="/support" 
            element={user ? <LiveSupport user={user} onLogout={handleLogout} /> : <Navigate to="/" />} 
          />
          <Route 
            path="/order-success" 
            element={user ? <OrderSuccess /> : <Navigate to="/" />} 
          />
          <Route path="/system/admin" element={<AdminLogin />} />
          <Route path="/system/admin/panel" element={<AdminPanel />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/license" element={<License />} />
          <Route path="/pricing" element={<Pricing />} />
          {/* SEO Landing Pages */}
          <Route path="/buy-dexscreener-reactions" element={<BuyDexscreenerReactions />} />
          <Route path="/buy-dexmoji" element={<BuyDexmoji />} />
          <Route path="/dexscreener-reaction" element={<DexscreenerReaction />} />
          <Route path="/buy-rocket-dexscreener" element={<BuyRocketDexscreener />} />
          <Route path="/boost-dexscreener" element={<BoostDexscreener />} />
          {/* New SEO Landing Pages */}
          <Route path="/buy-dexscreener-rockets" element={<BuyDexscreenerRockets />} />
          <Route path="/buy-dexscreener-fire" element={<BuyDexscreenerFire />} />
          <Route path="/buy-dexscreener-poop" element={<BuyDexscreenerPoop />} />
          <Route path="/dexreactions" element={<Dexreactions />} />
          <Route path="/cheap-dexscreener-reactions" element={<CheapDexscreenerReactions />} />
          <Route path="/buydexrockets" element={<BuyDexRockets />} />
          {/* Guest Order Status Page - No login required */}
          <Route path="/order/:orderId" element={<OrderStatus />} />
          {/* API Developer Public Page */}
          <Route path="/api-developer" element={<ApiDeveloper />} />
          {/* 404 - Catch all unmatched routes */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </SolanaProvider>
  );
}

export default App;
